import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc, query, onSnapshot, Timestamp, writeBatch } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { ITEM_TYPES } from '../constants';
import { processItemFormData, errorMessage } from '../helpers';
import { calculateMonthlyPayment } from '../logic/engine';
import { getValueField } from '../logic/review';

// F28 — `valueAsOf` records when the user last *confirmed* an item's value, and
// is the anchor every review estimate rolls from. It must move only when
// the value itself moves: renames, flag toggles and assumption edits leave it
// alone, otherwise editing a growth rate would silently reset the clock.
// `confirmedAt` lets the demo stamp a plain Date instead of a Firestore
// Timestamp — nothing in demo mode ever reaches Firestore, and `toDate()` in
// the engine reads both, so the demo has no reason to carry Firestore types.
const stampValueAsOf = (processedItem, previousItem, confirmedAt = null) => {
    const field = getValueField(processedItem);
    if (!field) return processedItem;
    const previous = previousItem ? parseFloat(previousItem[field]) : NaN;
    const next = parseFloat(processedItem[field]);
    if (!isNaN(previous) && !isNaN(next) && previous === next) return processedItem;
    return { ...processedItem, valueAsOf: confirmedAt || Timestamp.now() };
};

// D2 — ids for items the visitor adds in the demo. Only has to be unique
// within one in-memory session.
let demoItemSeq = 0;
const nextDemoItemId = () => `demo-added-${Date.now()}-${++demoItemSeq}`;

// D2 — a demo item keeps following the UI language only while the visitor has
// not renamed it. `nameKey` is that marker, so it survives an edit that left
// the name alone and is dropped by one that changed it.
const withDemoName = (existing, processedItem) => {
    const keepsKey = existing.nameKey && processedItem.name === existing.name;
    const merged = { ...existing, ...processedItem, updatedAt: new Date() };
    if (!keepsKey) delete merged.nameKey;
    return merged;
};

export const usePortfolio = (userId, isDemoMode, showModal, demoState, t, currentSnapshotId = null, activeSnapshot = null, updateSnapshot = null) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (currentSnapshotId && activeSnapshot) {
            setItems(activeSnapshot.portfolioItems || []);
            setLoading(false);
            return;
        }

        if (isDemoMode) {
            const processedDemoItems = (demoState.demoItems || []).map(item => {
                let processedItem = {...item};
                if (item.type === ITEM_TYPES.LOAN && !item.monthlyPayment) {
                    processedItem.monthlyPayment = calculateMonthlyPayment(item.loanAmount, item.interestRate, item.loanTermTotalMonths).toFixed(0);
                } else if (item.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage && !item.mortgageMonthlyPayment) {
                    processedItem.mortgageMonthlyPayment = calculateMonthlyPayment(item.mortgageInitialAmount, item.mortgageInterestRate, item.mortgageTermTotalMonths).toFixed(0);
                }
                return processedItem;
            });
            setItems(processedDemoItems.map(item => ({ ...item, createdAt: Timestamp.now(), updatedAt: Timestamp.now() })));
            setLoading(false);
            return;
        }

        if (!userId) {
            setItems([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const itemsCollectionPath = `artifacts/${appId}/users/${userId}/portfolioItems`;
        const qItems = query(collection(db, itemsCollectionPath));
        const unsubscribe = onSnapshot(qItems, (querySnapshot) => {
            setItems(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching items:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId, isDemoMode, demoState.demoItems, currentSnapshotId, activeSnapshot]);

    const persistSnapshotItems = useCallback((newItems) => {
        setItems(newItems);
        if (currentSnapshotId && typeof updateSnapshot === 'function') {
            updateSnapshot(currentSnapshotId, { portfolioItems: newItems });
        }
    }, [currentSnapshotId, updateSnapshot]);

    // D2 — the demo's own store. `items` is derived from `demoItems` by the
    // effect above, so writing here re-renders everything downstream exactly as
    // a Firestore listener would.
    const writeDemoItems = useCallback((updater) => {
        demoState.setDemoItems(updater);
    }, [demoState]);

    // D2 — the ONE thing the demo still refuses, and the reason it has to:
    // a snapshot is a persisted historical record by definition, so "change it,
    // but only in memory" is not a coherent offer. Checked before the demo
    // branch in every mutator; the snapshot branch below it is for real
    // accounts only.
    const blockedBySnapshot = useCallback(() => {
        if (isDemoMode && currentSnapshotId) {
            showModal(t('warnings.demoSnapshotReadOnly'));
            return true;
        }
        return false;
    }, [isDemoMode, currentSnapshotId, showModal, t]);

    const handleAddItem = useCallback(async (item, callbacks) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            const processedItem = stampValueAsOf(processItemFormData(item), null, new Date());
            writeDemoItems(prev => [...prev, {
                ...processedItem,
                id: processedItem.id || nextDemoItemId(),
                isActive: processedItem.isActive !== false,
                createdAt: new Date(),
                updatedAt: new Date(),
            }]);
            if (callbacks?.onSuccess) callbacks.onSuccess();
            return;
        }
        if (currentSnapshotId) {
            const processedItem = stampValueAsOf(processItemFormData(item), null);
            const newItem = {
                ...processedItem,
                id: processedItem.id || `snap-${Date.now()}`,
                isActive: processedItem.isActive !== false,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };
            persistSnapshotItems([...(items || []), newItem]);
            if (callbacks?.onSuccess) callbacks.onSuccess();
            return;
        }
        if (!userId) { return; }

        const processedItem = stampValueAsOf(processItemFormData(item), null);

        try {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/portfolioItems`), { ...processedItem, isActive: true, createdAt: Timestamp.now(), updatedAt: Timestamp.now(), userId: userId });
            callbacks.onSuccess();
        } catch (error) {
            console.error("Error adding document: ", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    const handleUpdateItem = useCallback(async (item, callbacks) => {
        const previousItem = (items || []).find(i => i.id === item.id) || null;
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            const processedItem = stampValueAsOf(processItemFormData(item), previousItem, new Date());
            writeDemoItems(prev => prev.map(i => (i.id === item.id ? withDemoName(i, processedItem) : i)));
            if (callbacks?.onSuccess) callbacks.onSuccess();
            return;
        }
        if (currentSnapshotId) {
            const processedItem = stampValueAsOf(processItemFormData(item), previousItem);
            const updatedItems = (items || []).map(i => i.id === item.id ? { ...i, ...processedItem, updatedAt: Timestamp.now() } : i);
            persistSnapshotItems(updatedItems);
            if (callbacks?.onSuccess) callbacks.onSuccess();
            return;
        }
        if (!userId || !item.id) { showModal(t('warnings.missingId')); return; }

        const processedItem = stampValueAsOf(processItemFormData(item), previousItem);

        try {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/portfolioItems`, item.id), { ...processedItem, updatedAt: Timestamp.now() });
            callbacks.onSuccess();
        } catch (error) {
            console.error("Error updating document: ", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    // F28 — settling a row on the review page. `newValue` is the number to
    // write, or null for "still correct": the value the user already stands
    // behind keeps its place and only its confirmation date moves, so the page
    // stops asking about it until it ages again. Taking the estimate and typing
    // a correction are the same write with a different number.
    const confirmItemValue = useCallback(async (row, newValue) => {
        if (!row || row.kind !== 'item') return;
        if (blockedBySnapshot()) return;
        const confirmedAt = isDemoMode ? new Date() : Timestamp.now();
        const patch = newValue == null
            ? { valueAsOf: confirmedAt }
            : { [row.field]: newValue, valueAsOf: confirmedAt };

        if (isDemoMode) {
            // D2 — the review page settles rows in the demo too, so the whole
            // Apply / Still correct workflow is demonstrable.
            writeDemoItems(prev => prev.map(i => (i.id === row.itemId ? { ...i, ...patch, updatedAt: confirmedAt } : i)));
            return;
        }
        if (currentSnapshotId) {
            persistSnapshotItems((items || []).map(i => i.id === row.itemId ? { ...i, ...patch, updatedAt: Timestamp.now() } : i));
            return;
        }
        if (!userId || !row.itemId) { return; }
        try {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/portfolioItems`, row.itemId), { ...patch, updatedAt: Timestamp.now() });
        } catch (error) {
            console.error("Error confirming item value:", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    // `confirm: false` is for onboarding's chip ✕: the item was added seconds
    // ago on the same screen, so undoing it needs no "are you sure".
    const handleDeleteItem = useCallback(async (itemId, { confirm = true } = {}) => {
        if (blockedBySnapshot()) return;
        // Names what goes, and the button says "Delete" in red.
        const askThen = (run) => {
            if (!confirm) { run(); return; }
            const name = (items || []).find(i => i.id === itemId)?.name;
            showModal(name ? t('warnings.deleteItemNamed', { name }) : t('warnings.deleteConfirmation'), true, run, null, null, 'general.delete', 'general.cancel', true);
        };
        if (isDemoMode) {
            // Asks first, like the real thing — the confirmation is part of what
            // the demo is showing.
            askThen(() => writeDemoItems(prev => prev.filter(i => i.id !== itemId)));
            return;
        }
        if (currentSnapshotId) {
            askThen(() => persistSnapshotItems((items || []).filter(i => i.id !== itemId)));
            return;
        }
        if (!userId || !itemId) { showModal(t('warnings.missingId')); return; }
        const remove = async () => {
            try {
                await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/portfolioItems`, itemId));
            } catch (error) {
                console.error("Error deleting document: ", error);
                showModal(errorMessage(t, error));
            }
        };
        askThen(remove);
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    const handleItemActiveToggle = useCallback(async (itemId, isActive) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            writeDemoItems(prevItems => prevItems.map(i => i.id === itemId ? { ...i, isActive } : i));
            return;
        }
        if (currentSnapshotId) {
            const updatedItems = (items || []).map(i => i.id === itemId ? { ...i, isActive, updatedAt: Timestamp.now() } : i);
            persistSnapshotItems(updatedItems);
            return;
        }
        if (!userId) { return; }
        try {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/portfolioItems`, itemId), { isActive: isActive });
        } catch (error) {
            console.error("Error toggling item active state:", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    /**
     * Whether an item counts toward investable assets. Same three paths as the
     * active toggle — a snapshot rewrites its own copy, demo mode stays local,
     * a real account writes one field — because it is the same kind of edit
     * reached from the Independence page's breakdown rather than the item form.
     */
    const handleItemFireToggle = useCallback(async (itemId, includeInFire) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            writeDemoItems(prevItems => prevItems.map(i => i.id === itemId ? { ...i, includeInFire } : i));
            return;
        }
        if (currentSnapshotId) {
            const updatedItems = (items || []).map(i => i.id === itemId ? { ...i, includeInFire, updatedAt: Timestamp.now() } : i);
            persistSnapshotItems(updatedItems);
            return;
        }
        if (!userId) { return; }
        try {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/portfolioItems`, itemId), { includeInFire });
        } catch (error) {
            console.error("Error toggling item FIRE inclusion:", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotItems, items, blockedBySnapshot, writeDemoItems]);

    const handleBulkAction = useCallback(async (actionType) => {
        if (blockedBySnapshot()) return;
        // Activating and deactivating are undone by the opposite button, so
        // they just happen; only the deletes ask, and say so in red.
        const reversible = actionType === 'activateAll' || actionType === 'deactivateAll';
        const askThen = (key, count, run) => reversible
            ? run()
            : showModal(t(key, { count }), true, run, null, null, 'general.delete', 'general.cancel', true);
        // D2 — bulk actions in the demo. Shares its selection and confirmation
        // wording with the two branches below; only the write differs.
        if (isDemoMode) {
            const plans = {
                activateAll: { pick: (i) => i.isActive === false, empty: 'warnings.allAlreadyActive', ask: 'warnings.bulkActivate' },
                deactivateAll: { pick: (i) => i.isActive !== false, empty: 'warnings.allAlreadyInactive', ask: 'warnings.bulkDeactivate' },
                deleteInactive: { pick: (i) => i.isActive === false, empty: 'warnings.noInactiveToDelete', ask: 'warnings.bulkDeleteInactive' },
                deleteAll: { pick: () => true, empty: 'warnings.portfolioEmpty', ask: 'warnings.bulkDeleteAll' },
            };
            const plan = plans[actionType];
            if (!plan) return;
            const affected = (items || []).filter(plan.pick);
            if (affected.length === 0) { showModal(t(plan.empty)); return; }
            askThen(plan.ask, affected.length, () => {
                writeDemoItems(prev => {
                    if (actionType === 'activateAll') return prev.map(i => i.isActive === false ? { ...i, isActive: true } : i);
                    if (actionType === 'deactivateAll') return prev.map(i => i.isActive !== false ? { ...i, isActive: false } : i);
                    if (actionType === 'deleteInactive') return prev.filter(i => i.isActive !== false);
                    return [];
                });
            });
            return;
        }
        if (currentSnapshotId) {
            let itemsToProcess = [];
            let confirmationMessageKey = '';

            switch (actionType) {
                case 'activateAll':
                    itemsToProcess = items.filter(item => item.isActive === false);
                    if (itemsToProcess.length === 0) { showModal(t('warnings.allAlreadyActive')); return; }
                    confirmationMessageKey = 'warnings.bulkActivate';
                    break;
                case 'deactivateAll':
                    itemsToProcess = items.filter(item => item.isActive !== false);
                    if (itemsToProcess.length === 0) { showModal(t('warnings.allAlreadyInactive')); return; }
                    confirmationMessageKey = 'warnings.bulkDeactivate';
                    break;
                case 'deleteInactive':
                    itemsToProcess = items.filter(item => item.isActive === false);
                    if (itemsToProcess.length === 0) { showModal(t('warnings.noInactiveToDelete')); return; }
                    confirmationMessageKey = 'warnings.bulkDeleteInactive';
                    break;
                case 'deleteAll':
                    itemsToProcess = items;
                    if (itemsToProcess.length === 0) { showModal(t('warnings.portfolioEmpty')); return; }
                    confirmationMessageKey = 'warnings.bulkDeleteAll';
                    break;
                default:
                    return;
            }

            askThen(confirmationMessageKey, itemsToProcess.length, async () => {
                let updatedItems = [...items];
                if (actionType === 'activateAll') {
                    updatedItems = updatedItems.map(item => item.isActive === false ? { ...item, isActive: true, updatedAt: Timestamp.now() } : item);
                } else if (actionType === 'deactivateAll') {
                    updatedItems = updatedItems.map(item => item.isActive !== false ? { ...item, isActive: false, updatedAt: Timestamp.now() } : item);
                } else if (actionType === 'deleteInactive') {
                    updatedItems = updatedItems.filter(item => item.isActive !== false);
                } else if (actionType === 'deleteAll') {
                    updatedItems = [];
                }
                persistSnapshotItems(updatedItems);
            });
            return;
        }
        if (!userId) {
            return;
        }

        const itemsCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/portfolioItems`);
        const batch = writeBatch(db);
        let confirmationMessageKey = '';
        let itemsToProcess = [];

        switch (actionType) {
            case 'activateAll':
                itemsToProcess = items.filter(item => item.isActive === false);
                if (itemsToProcess.length === 0) { showModal(t('warnings.allAlreadyActive')); return; }
                confirmationMessageKey = 'warnings.bulkActivate';
                break;
            case 'deactivateAll':
                itemsToProcess = items.filter(item => item.isActive !== false);
                 if (itemsToProcess.length === 0) { showModal(t('warnings.allAlreadyInactive')); return; }
                confirmationMessageKey = 'warnings.bulkDeactivate';
                break;
            case 'deleteInactive':
                itemsToProcess = items.filter(item => item.isActive === false);
                if (itemsToProcess.length === 0) { showModal(t('warnings.noInactiveToDelete')); return; }
                confirmationMessageKey = 'warnings.bulkDeleteInactive';
                break;
            case 'deleteAll':
                 itemsToProcess = items;
                 if (itemsToProcess.length === 0) { showModal(t('warnings.portfolioEmpty')); return; }
                confirmationMessageKey = 'warnings.bulkDeleteAll';
                break;
            default:
                return;
        }

        askThen(confirmationMessageKey, itemsToProcess.length, async () => {
            try {
                itemsToProcess.forEach(item => {
                    const docRef = doc(itemsCollectionRef, item.id);
                    if (actionType === 'activateAll') {
                        batch.update(docRef, { isActive: true });
                    } else if (actionType === 'deactivateAll') {
                        batch.update(docRef, { isActive: false });
                    } else if (actionType === 'deleteInactive' || actionType === 'deleteAll') {
                        batch.delete(docRef);
                    }
                });
                await batch.commit();
            } catch (error) {
                console.error("Bulk action failed: ", error);
                showModal(errorMessage(t, error));
            }
        });

    }, [userId, isDemoMode, showModal, items, t, currentSnapshotId, persistSnapshotItems, blockedBySnapshot, writeDemoItems]);
    
    return { items, loading, handleAddItem, handleUpdateItem, handleDeleteItem, handleItemActiveToggle, handleItemFireToggle, handleBulkAction, confirmItemValue };
};